import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import useI18n from 'hooks/useI18n'
import { ReadCompetitiveIntelligenceResearchDetail } from 'services/competitive-intelligence'
import { trackEvent, trackEventOnce } from 'services/analytics'
import { apiRequest, exposeStrapiError } from 'utils'

const LANGUAGE_LOCALES = {
    Chinese: 'zh-CN', English: 'en-US', German: 'de-DE', Hindi: 'hi-IN',
    Japanese: 'ja-JP', Portuguese: 'pt-BR', Spanish: 'es-ES',
}
const errorStatus = result => Number(result?.error?.status || 0)

export default function useController() {
    const { language, t } = useI18n()
    const history = useHistory()
    const location = useLocation()
    const { id } = useParams()
    const [state, setState] = useState({ status: 'loading', record: null })

    const load = useCallback(async () => {
        setState({ status: 'loading', record: null })
        const result = await apiRequest(() => ReadCompetitiveIntelligenceResearchDetail(id))
        if (!result || result?.error) {
            const code = errorStatus(result)
            if (result?.error && ![401, 403, 404].includes(code)) exposeStrapiError(result)
            setState({
                status: [401, 403].includes(code) ? 'denied' : code === 404 ? 'notFound' : code >= 500 || !result ? 'unavailable' : 'error',
                record: null,
            })
            return
        }
        trackEventOnce('competitive_intelligence_research_record_opened', {
            competitive_section: 'research', content_type: result.data?.recordType || 'research', content_id: result.data?.id,
            competitor_id: result.data?.competitor, competitive_source: result.data?.source,
        }, `research-detail:${result.data?.id}`)
        setState({ status: 'ready', record: result.data })
    }, [id])

    useEffect(() => { load() }, [load])

    const locale = LANGUAGE_LOCALES[language] || 'en-US'
    const formatCount = useMemo(() => new Intl.NumberFormat(locale).format, [locale])
    const formatDate = useCallback((value, withTime = false) => {
        if (!value) return t('competitive_intelligence_freshness_unknown')
        const parsed = new Date(value.length === 10 ? `${value}T00:00:00` : value)
        return Number.isNaN(parsed.getTime()) ? t('competitive_intelligence_freshness_unknown') : new Intl.DateTimeFormat(locale, withTime ? { dateStyle: 'medium', timeStyle: 'short' } : { dateStyle: 'medium' }).format(parsed)
    }, [locale, t])

    const onBack = useCallback(() => history.push(`/competitive-intelligence/research${location.search}`), [history, location.search])
    const onOpenSource = useCallback(() => trackEvent('competitive_intelligence_research_original_source_opened', {
        competitive_section: 'research', content_type: state.record?.recordType || 'research', content_id: state.record?.id,
        competitor_id: state.record?.competitor, competitive_source: state.record?.source,
    }), [state.record])

    return { ...state, formatCount, formatDate, onBack, onOpenSource, retry: load, t }
}
